/**
 * API client — Documentos legais versionados (LegalDocumentVersion)
 *
 * Rotas admin (superadmin) em /api/internal/legal-documents.
 * Rotas do usuário final (leitura/aceite/download) em /api/terms.
 */

import { authFetch } from '@/utils/api';

const ADMIN_BASE = '/api/internal/legal-documents';
const USER_BASE = '/api/terms';

async function parse(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || data.message || 'Erro na requisição');
        err.status = res.status;
        err.code = data.code;
        err.data = data;
        throw err;
    }
    return data;
}

// ─── Admin (superadmin) ───────────────────────────────────────────────────────

export async function listVersions({ type, status } = {}) {
    const qs = new URLSearchParams();
    if (type) qs.set('type', type);
    if (status) qs.set('status', status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return parse(await authFetch(`${ADMIN_BASE}${suffix}`));
}

export async function getVersion(id) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}`));
}

export async function createVersion(payload) {
    return parse(await authFetch(ADMIN_BASE, { method: 'POST', body: JSON.stringify(payload) }));
}

export async function updateVersion(id, payload) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}`, { method: 'PUT', body: JSON.stringify(payload) }));
}

export async function publishVersion(id) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}/publish`, { method: 'POST', body: '{}' }));
}

/** Edita o documento vigente gerando uma nova versão publicada (arquiva a anterior). */
export async function reviseVersion(id, payload) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}/revise`, { method: 'POST', body: JSON.stringify(payload) }));
}

export async function archiveVersion(id) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}/archive`, { method: 'POST', body: '{}' }));
}

export async function deleteVersion(id) {
    return parse(await authFetch(`${ADMIN_BASE}/${id}`, { method: 'DELETE' }));
}

// ─── Usuário final ────────────────────────────────────────────────────────────

/** Lista as versões vigentes (sem conteúdo). */
export async function listPublished() {
    return parse(await authFetch(`${USER_BASE}/documents`));
}

/** Versão vigente de um tipo, com o conteúdo Markdown. */
export async function getPublished(type) {
    return parse(await authFetch(`${USER_BASE}/documents/${type}`));
}

/** Status do portão de aceite por conta. */
export async function getGateStatus() {
    return parse(await authFetch(`${USER_BASE}/gate-status`));
}

/** Registra o aceite (apenas admin; vale para a conta inteira). */
export async function acceptTerms({ termsVersion, termsType, cnpj }) {
    return parse(
        await authFetch(`${USER_BASE}/accept`, {
            method: 'POST',
            body: JSON.stringify({ termsVersion, termsType, cnpj }),
        }),
    );
}

/**
 * Baixa o PDF do documento vigente de um tipo e dispara o download no browser.
 * Registra o IP no backend (prova legal).
 */
export async function downloadDocument(type, filename) {
    const res = await authFetch(`${USER_BASE}/documents/${type}/download`);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const err = new Error(data.error || 'Falha ao baixar o documento');
        err.status = res.status;
        throw err;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `${type}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export default {
    listVersions,
    getVersion,
    createVersion,
    updateVersion,
    publishVersion,
    reviseVersion,
    archiveVersion,
    deleteVersion,
    listPublished,
    getPublished,
    getGateStatus,
    acceptTerms,
    downloadDocument,
};
